import { type FC, type ReactNode, useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { NoSpecSelected } from '../../components/NoSpecSelected';
import { useSpec } from '../../contexts/SpecContext';
import { useGenerationSocket } from '../../hooks/useGenerationSocket';
import { CollectionRequestList } from './CollectionRequestList';
import { GenerationPanel } from './GenerationPanel';
import { PostmanFilesInfo } from './PostmanFilesInfo';
import styles from './BusinessFlow.module.css';

const API_URL = import.meta.env.VITE_API_URL || '';

interface BusinessFlowProps {
  onOpenRightDrawer?: (content: { title: string; component: ReactNode }) => void;
}

export const BusinessFlow: FC<BusinessFlowProps> = ({ onOpenRightDrawer: _onOpenRightDrawer }) => {
  const { hasActiveSpec, activeSpec, isPostman } = useSpec();
  const [loading, setLoading] = useState<boolean>(true);
  const [postmanCollection, setPostmanCollection] = useState<any>(null);
  const [postmanEnv, setPostmanEnv] = useState<any>(null);
  const [uploading, setUploading] = useState<boolean>(false);

  const countPostmanRequests = useCallback((items: any[]): number => {
    const countItems = (list: any[]): number => {
      let c = 0;
      for (const item of list) {
        if (item.request) c++;
        if (Array.isArray(item.item)) c += countItems(item.item);
      }
      return c;
    };
    return countItems(items);
  }, []);

  const loadPostmanFiles = useCallback(async () => {
    setLoading(true);
    try {
      if (isPostman) {
        const response = await fetch(`${API_URL}/api/schemas/${activeSpec.id}`);
        const data = await response.json();
        if (response.ok && data.success && data.schema?.content) {
          const parsed = JSON.parse(data.schema.content);
          const root = parsed.collection || parsed;
          root.requestCount = countPostmanRequests(root.item || []);
          setPostmanCollection(root);
        }
        const envResponse = await fetch(`${API_URL}/api/postman/check/${activeSpec.id}`);
        const envData = await envResponse.json();
        if (envResponse.ok && envData.success) {
          setPostmanEnv(envData.environment);
        }
      } else {
        const response = await fetch(`${API_URL}/api/postman/check/${activeSpec.id}`);
        const data = await response.json();
        if (response.ok && data.success) {
          setPostmanCollection(data.collection);
          setPostmanEnv(data.environment);
        }
      }
    } catch (error) {
      console.error('Error loading Postman files:', error);
    } finally {
      setLoading(false);
    }
  }, [isPostman, activeSpec, countPostmanRequests]);

  useEffect(() => {
    if (hasActiveSpec && activeSpec) {
      loadPostmanFiles();
    }
  }, [hasActiveSpec, activeSpec, loadPostmanFiles]);

  const { generating, consoleOutput, generationStatus, startGeneration, failGeneration, stopGeneration } =
    useGenerationSocket({
      eventPrefix: 'postman-generation',
      schemaId: activeSpec?.id,
      onComplete: loadPostmanFiles,
    });

  const handleGenerateCollection = async () => {
    if (!activeSpec) {
      toast.error('No active specification selected');
      return;
    }

    startGeneration('Starting Postman collection generation...\n');

    try {
      const response = await fetch(`${API_URL}/api/postman/generate/${activeSpec.id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        failGeneration(`Error: ${data.message || 'Failed to start generation'}`);
        toast.error('Failed to start Postman generation');
      }
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Unknown error';
      console.error('[Generate] Error starting Postman generation:', error);
      failGeneration(`Error: ${message}`);
      toast.error('Failed to start Postman generation');
    }
  };

  const handleStopGeneration = async () => {
    if (!activeSpec) return;

    try {
      await fetch(`${API_URL}/api/postman/generate/${activeSpec.id}/stop`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      stopGeneration();
    } catch (error) {
      console.error('Error stopping Postman generation:', error);
    }
  };

  const handleDownload = async (type: string): Promise<void> => {
    try {
      const response = await fetch(`${API_URL}/api/postman/download/${activeSpec.id}/${type}`);

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = type === 'collection' ? 'postman.json' : 'postman-env.json';
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        toast.success(`${type === 'collection' ? 'Collection' : 'Environment'} downloaded successfully`);
      } else {
        toast.error('Failed to download file');
      }
    } catch (error) {
      console.error('Error downloading file:', error);
      toast.error('Failed to download file');
    }
  };

  const handleUploadCollection = async (parsed: any): Promise<void> => {
    setUploading(true);
    try {
      const response = await fetch(`${API_URL}/api/postman/upload/${activeSpec.id}/collection`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(parsed),
      });

      const data = await response.json();
      if (response.ok && data.success) {
        toast.success('Postman collection uploaded successfully');
        loadPostmanFiles();
      } else {
        toast.error(data.message || 'Failed to upload collection');
      }
    } catch (error) {
      console.error('Error uploading collection:', error);
      toast.error('Failed to upload collection');
    } finally {
      setUploading(false);
    }
  };

  const handleUploadEnvironment = async (parsed: any): Promise<void> => {
    setUploading(true);
    try {
      const response = await fetch(`${API_URL}/api/postman/upload/${activeSpec.id}/environment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(parsed),
      });

      const data = await response.json();
      if (response.ok && data.success) {
        toast.success('Postman environment uploaded successfully');
        loadPostmanFiles();
      } else {
        toast.error(data.message || 'Failed to upload environment');
      }
    } catch (error) {
      console.error('Error uploading environment:', error);
      toast.error('Failed to upload environment');
    } finally {
      setUploading(false);
    }
  };

  if (!hasActiveSpec) {
    return <NoSpecSelected />;
  }

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.emptyState}>
          <h3>Loading...</h3>
        </div>
      </div>
    );
  }

  const hasPostmanFiles = postmanCollection || postmanEnv;

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1 className={styles.title}>Business Flow</h1>
        <p className={styles.description}>
          {isPostman
            ? 'View and manage the Postman collection from your uploaded specification.'
            : 'Generate Postman collections with business flow scenarios from your OpenAPI specifications to quickly test and explore your API endpoints.'}
        </p>
      </div>

      {!isPostman && (
        <GenerationPanel
          activeSpec={activeSpec}
          loading={loading}
          generating={generating}
          uploading={uploading}
          consoleOutput={consoleOutput}
          generationStatus={generationStatus}
          onGenerate={handleGenerateCollection}
          onStop={handleStopGeneration}
          onUploadCollection={handleUploadCollection}
        />
      )}

      {isPostman && !hasPostmanFiles && (
        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>Postman Collection</h2>
          <div className={styles.infoCard}>
            <h3 className={styles.infoCardTitle}>
              Collection
              <span className={styles.statusBadge} data-status="not-found">
                Not Found
              </span>
            </h3>
            <p className={styles.infoText}>Could not load the Postman collection from the uploaded specification.</p>
          </div>
        </div>
      )}

      <PostmanFilesInfo
        postmanCollection={postmanCollection}
        postmanEnv={postmanEnv}
        activeSpec={activeSpec}
        uploading={uploading}
        onDownload={handleDownload}
        onUploadEnvironment={handleUploadEnvironment}
      />

      {postmanCollection?.item && <CollectionRequestList items={postmanCollection.item} />}
    </div>
  );
};
